import { RemoteConfigModel } from 'src/models/remoteConfig.models'

/**
 * Check if SSO Login feature is enabled for the user.
 * Returns true if either:
 * 1. The global enable_sso_login flag is enabled, OR
 * 2. The user's organization ID is in the enable_sso_login_whitelist
 *
 * Note: On the sign-in page, organizationId may not be available yet.
 * In that case, only the global flag is checked.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional for sign-in page)
 * @returns true if SSO Login is enabled for this user
 */
export const isSSOLoginEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { enable_sso_login, enable_sso_login_whitelist } = remoteConfig

  // If org ID is available, check the whitelist
  if (organizationId) {
    return (
      enable_sso_login || enable_sso_login_whitelist.includes(organizationId)
    )
  }

  // On sign-in page (no org ID), only use the global flag
  return enable_sso_login
}

/**
 * Check if AI Insights feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_ai_insights flag is enabled, OR
 * 2. The user's organization ID is in the ai_insights_allowlist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if AI Insights is enabled for this user
 */
export const isAIInsightsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_ai_insights, ai_insights_allowlist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_ai_insights || ai_insights_allowlist.includes(organizationId)
}

/**
 * Check if Extend AI Agent feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_extend_ai_agent flag is enabled, OR
 * 2. The user's organization ID is in the extendAIWhitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if Extend AI Agent is enabled for this user
 */
export const isExtendAIEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_extend_ai_agent, extendAIWhitelist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_extend_ai_agent || extendAIWhitelist.includes(organizationId)
}

/**
 * Check if the spend reporting dashboard is enabled for the user.
 * Returns true if either:
 * 1. The global enable_spend_reporting_dashboard flag is enabled, OR
 * 2. The user's organization ID is in the
 *    enable_spend_reporting_dashboard_whitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if the spend reporting dashboard is enabled for this user
 */
export const isSpendReportingDashboardEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const {
    enable_spend_reporting_dashboard,
    enable_spend_reporting_dashboard_whitelist,
  } = remoteConfig

  return (
    enable_spend_reporting_dashboard ||
    enable_spend_reporting_dashboard_whitelist.includes(organizationId)
  )
}

/** Show transaction comments when the client feature flag is enabled. */
export const isTransactionConversationsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.enable_transaction_conversations
}

export const isExpensePolicyAIEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { show_expense_policy_ai, show_expense_policy_ai_whitelist } =
    remoteConfig

  if (organizationId) {
    return (
      show_expense_policy_ai ||
      show_expense_policy_ai_whitelist.includes(organizationId)
    )
  }

  return show_expense_policy_ai
}

export const isExpensePolicyAIChatEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.show_expense_policy_ai_chat
}

/**
 * Check if the redesigned physical card Account Settings tiles are enabled.
 * Gates the new bento layout (Card Status & Lifecycle, Spend Limit, etc.) on
 * the physical card details page. EX-40354.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @returns true if the updated physical card settings UI is enabled
 */
export const isUpdatedPhysicalCardSettingsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.show_updated_physical_card_settings
}

/**
 * Check if Suggestions and Predictions feature is enabled for the user.
 * Requires all of:
 * 1. The global show_suggestions_and_predictions kill switch is enabled, AND
 * 2. The user has an active ERP integration.
 *
 * Non-SaaS issuer exclusion is handled upstream by the backend
 * AI_EXPENSE_CATEGORIES entitlement; this util only enforces the kill switch
 * and ERP-connected requirement.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @param isErpConnected - Whether the org has an active ERP integration
 * @returns true if Suggestions and Predictions is enabled for this user
 */
export const isSuggestionsAndPredictionsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
  isErpConnected: boolean,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  return remoteConfig.show_suggestions_and_predictions && isErpConnected
}

/**
 * Check if the ERP disconnect AI-loss warning modal is enabled.
 * EX-39491 — when on, the final disconnect confirmation shows a scary
 * AI-aware warning and requires the admin to type "DISCONNECT".
 */
export const isErpDisconnectAiWarningEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.show_erp_disconnect_ai_warning
}

/**
 * Whole-feature Bill Pay gate (the new ACH/wire/check bank-account rails that
 * replace the legacy virtual-card bill pay — intentionally NOT gated by the
 * legacy show_bill_pay_* flags). Returns true if either:
 * 1. The global `bill_pay` flag is enabled, OR
 * 2. The org is on `bill_pay_allowlist` — the ONE milestone-agnostic Bill Pay
 *    beta list (an org on it is "in the Bill Pay beta", full stop).
 *
 * This gate alone shows nothing: each milestone surface ANDs it with its own
 * `bill_pay_mX_*` readiness bool (isBillPayM1BankAccountsEnabled,
 * isBillPayM2VendorsEnabled). Beta orgs receive each milestone the moment its
 * bool flips, with no per-milestone allowlists to maintain.
 *
 * When no organizationId is supplied (defensive / future caller) only the global
 * flag is checked. Never throws: a missing allowlist falls back to an empty list.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if this user's org passes the whole-feature Bill Pay gate
 */
export const isBillPayEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { bill_pay, bill_pay_allowlist } = remoteConfig

  // If org ID is available, also honor the per-org allowlist
  if (organizationId) {
    return bill_pay || (bill_pay_allowlist ?? []).includes(organizationId)
  }

  // No org ID (defensive / future caller): only use the global flag
  return bill_pay
}

/**
 * Check if Bill Pay M1 (Bank Accounts — the org funding-account experience) is
 * enabled for the user: (whole-feature gate) AND (`bill_pay_m1_bank_accounts`
 * "M1 is ready" bool). The bool keeps milestones releasable independently.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if Bill Pay Bank Accounts is enabled for this user
 */
export const isBillPayM1BankAccountsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_m1_bank_accounts
  )
}

/**
 * Check if Bill Pay M2 (Vendors) is enabled for the user: (whole-feature gate)
 * AND (`bill_pay_m2_vendors` "M2 is ready" bool).
 *
 * The AND keeps milestones releasable independently (M1 can GA while M2 is
 * dark; while M2 is mock-backed/in-build the bool stays off so beta orgs don't
 * see it) and encodes the product dependency: Vendors cannot exist without the
 * Bill Pay bank-account experience.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if Bill Pay Vendors is enabled for this user
 */
export const isBillPayM2VendorsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_m2_vendors
  )
}
